<script>
import ListItem from "@/components/molecules/list/list-item";
import LazyImage from "@/components/atoms/lazy-image";
import LinkTo from "@/components/atoms/link-to";
import Button from "@/components/atoms/button";
import ProductListItemAdvantage from "@/containers/avantages";
import CartItemQuantityInputSpinner from "@/containers/cart-item-quantity-input-spinner";
import ShippingMethodEligibility from "@/containers/shipping-method-eligibility";
import ButtonIcon from "@/components/molecules/button-icon";
import PftPlageSelector from "@/containers/pft-plage-selector";
import FormCheckbox from "@/components/molecules/form-checkbox";
import Alert from "@/components/molecules/alert";
import Icon from "@/components/atoms/icon";
import PrixRemise from "@/containers/prix-remise";
import {
  productCategoryItemEmits,
  productCategoryItemProps,
  useProductCategoryItem
} from "./mixin";

export default {
  name: "ProductCategoryItem",
  components: {
    ListItem,
    LazyImage,
    LinkTo,
    ProductListItemAdvantage,
    CartItemQuantityInputSpinner,
    ShippingMethodEligibility,
    Button,
    ButtonIcon,
    PftPlageSelector,
    FormCheckbox,
    Alert,
    Icon,
    PrixRemise
  },
  props: {
    ...productCategoryItemProps()
  },
  emits: [
    "removeItem",
    "addToListeDeCourse",
    "showSimilaires",
    ...productCategoryItemEmits()
  ],
  setup(props, context) {
    const {
      prixTotalRemise,
      quantite,
      prixTotal,
      isPft,
      poids,
      prix,
      isPanierLdd,
      isCarteKdo,
      setQuantity,
      plageSelected,
      navigateToProductSheet,
      switchAccord,
      isBuffet
    } = useProductCategoryItem(props, context);

    const articleDescription = ref(null);

    return {
      prixTotalRemise,
      quantite,
      prixTotal,
      isPft,
      isBuffet,
      poids,
      prix,
      isPanierLdd,
      isCarteKdo,
      setQuantity,
      plageSelected,
      navigateToProductSheet,
      switchAccord,
      articleDescription
    };
  }
};
</script>

<template>
  <ListItem
    :class="isPanierLdd && 'c-product-category-item--ldd'"
    class="c-product-category-item"
  >
    <div class="u-text-right u-mb-xxs u-mb-sm@lg">
      <Button
        link
        variant="grey-3"
        @click="$emit('removeItem')"
      >
        <span class="u-font-13">Supprimer</span>
      </Button>
    </div>
    <div class="c-product-category-item__line">
      <LazyImage
        :src="article.visuel"
        :dimension="96"
        alt=""
        height="80"
        width="80"
        class="c-product-category-item__image"
      />
      <div class="c-product-category-item__infos">
        <div class="c-product-category-item__description">
          <LinkTo
            v-if="article.slug"
            ref="articleDescription"
            v-uid
            :to="article.slug"
            class="c-product-category-item__title"
            @click="navigateToProductSheet"
          >
            {{ truncate(article.designation, 60) }}
          </LinkTo>
          <p
            v-else
            class="c-product-category-item__title"
          >
            {{ truncate(article.designation, 60) }}
          </p>
          <p
            v-if="article.prix_unite_reference || poids"
            class="c-product-category-item__subtitle"
          >
            <template v-if="poids">
              {{ poids }}
              <template v-if="article.prix_unite_reference">
                |
              </template>
            </template>
            <template v-if="article.prix_unite_reference">
              {{ formatPrix(article.prix_unite_reference) }} €
              {{ article.unite_reference }}
            </template>
          </p>
          <p
            v-if="article.origine"
            class="c-product-category-item__subtitle u-text-uppercase"
          >
            {{ article.origine }}
          </p>
        </div>
        <div class="c-product-category-item__infos-wrap">
          <div class="c-product-category-item__commercial-infos">
            <ProductListItemAdvantage
              v-if="article.avantages"
              :advantages="article.avantages"
              :article="article"
              :cart-context="cartContext"
            />
          </div>
          <div class="c-product-category-item__prices">
            <p
              v-if="article.prix_barre && !article.prix_avec_remise_sans_appel_fidelite"
              class="c-product-category-item__price-crossed"
            >
              {{ formatPrix(article.prix_barre) }}&nbsp;€
            </p>
            <p
              v-if="prix"
              class="c-product-category-item__price-amount"
            >
              {{ formatPrix(prix) }}&nbsp;<span class="u-font-12">{{ isBuffet ? '€/ part': '€' }}</span>
            </p>
            <p
              v-if="article.prix_unite_reference && !isBuffet"
              class="c-product-category-item__unit-price"
            >
              {{ formatPrix(article.prix_unite_reference) }} €
              {{ article.unite_reference }}
            </p>
            <p
              v-if="isBuffet"
              class="c-product-category-item__unit-price"
            >
              {{ article.qte_min_panier }} parts minimum
            </p>
            <p
              v-if="isPanierLdd"
              class="c-product-category-item__price-delivery"
            >
              frais de livraison inclus
            </p>
          </div>
          <template v-if="article.en_stock">
            <template v-if="!isCarteKdo">
              <CartItemQuantityInputSpinner
                v-if="!isPft && !isBuffet"
                class="c-product-category-item__input-quantity u-mr-md"
                size="md-full-width"
                :value="article.quantite"
                :max="article.qte_max_panier"
                :display-type-panier="false"
                :aria-describedby="articleDescription?.$el?.id"
                @setQuantity="setQuantity"
                @removeItem="$emit('removeItem')"
              />
              <PftPlageSelector
                v-if="isPft || isBuffet"
                class="c-product-category-item_input-pft u-mr-md"
                :class="isBuffet && 'u-align-left'"
                :quantite="article.quantite"
                :show-button="false"
                :selected-index="article.indexPoids"
                :pft-conf="article.pft_conf"
                :article="article"
                :aria-describedby="articleDescription?.$el?.id"
                :pft-detail="isBuffet"
                :variant="isBuffet ? 'buffet' : 'panier'"
                @plageSelected="plageSelected"
                @addToCart="plageSelected"
              />
            </template>

            <div class="c-product-category-item__price-summary">
              <p class="c-product-category-item__price-total">
                {{ formatPrix(prixTotal) }}&nbsp;<span class="u-font-12">€</span>
              </p>
              <p
                v-if="article.eco_part"
                class="c-product-category-item__price-ecopart"
              >
                dont {{ formatPrix(article.eco_part) }}&nbsp;€ d'éco-part
              </p>
              <p
                v-if="article.eco_mobilier"
                class="c-product-category-item__price-ecopart"
              >
                dont {{ formatPrix(article.eco_mobilier) }}&nbsp;€ d'éco-mobilier
              </p>
            </div>
          </template>
          <template v-else>
            <div class="c-product-category-item__buttons">
              <ButtonIcon
                v-if="article.type !== 'indisponible'"
                class="c-product-category-item__button-whislit"
                icon-name="mes-listes"
                variant="primary"
                outline
                radius="full"
                icon-color="primary"
                @click="$emit('addToListeDeCourse')"
              />
              <Button
                variant="primary"
                radius="full"
                size="xs"
                @click="$emit('showSimilaires')"
              >
                <span class="u-font-400">Voir les produits similaires</span>
              </Button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div
      v-if="prixTotalRemise && prixTotalRemise !== article.prix_total_avec_remise"
      class="c-product-category-item__line"
    >
      <div class="c-product-category-item__remise">
        <PrixRemise
          price-size="md"
          :prix-remise="prixTotalRemise"
        />
      </div>
    </div>

    <div
      v-if="article.en_stock"
      class="c-product-category-item__line c-product-category-item__line--secondary"
    >
      <template v-if="showSubstitution && !isCarteKdo">
        <FormCheckbox
          v-if="article.is_substituable"
          :value="article.substitution_accord"
          @change="switchAccord"
        >
          <span class="u-color-text-light u-font-13 u-mt-xxs u-mr-xs">
            J’autorise la substitution
          </span>
        </FormCheckbox>
        <p
          v-else
          class="u-font-13 u-color-text-light u-mt-xxs u-mr-xs"
        >
          Ce produit ne peut pas être substitué
        </p>
      </template>
      <template v-if="!isPanierLdd">
        <ShippingMethodEligibility
          :unavailable-services="article.unavailable_services"
          infobulle
          open-on-hover
          class="c-product-category-item__shipping-eligibity-list"
        />
      </template>
    </div>

    <div
      v-if="article.en_stock && article.infos_vendeur"
      class="c-product-category-item__line"
    >
      <div class="c-product-category-item__alert-wrapper">
        <Alert
          v-if="article.en_stock"
          variant="grey-7"
          class="c-product-category-item__alert u-mt-sm"
        >
          <div class="u-flex u-flex-column">
            <div class="u-flex u-flex-align-center u-mb-xxs u-mr-md u-mr-none@sm">
              <Icon
                name="exclu-web"
                size="sm"
                class="u-flex-shrink-none u-flex-self-start"
              />
              <p class="u-font-700 u-font-13 u-ml-xs">
                Livraison sous {{ article.infos_vendeur.delai_livraison }} jours ouvrés à compter de la validation de votre commande
              </p>
            </div>
            <div class="u-ml-lg u-mr-md u-mr-none@sm">
              <p class="u-font-700 u-font-13 u-mb-xxs">
                Conditions particulières de livraison
              </p>
              <p class="u-font-12">
                {{ article.infos_vendeur.conditions_livraison }}
              </p>
            </div>
          </div>
        </Alert>
      </div>
    </div>

    <div
      v-if="article.id && showRecettes"
      style="display: none;"
      class="miamRecipesTag"
      :data-context="article.id"
    ></div>
  </ListItem>
</template>

<style scoped lang="scss">
@import "@/assets/styles/components/product-category-item.scss";
</style>
